import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, X, Loader2, AlertCircle } from "lucide-react";

/**
 * QRScannerDialog — full-screen modal that opens the back camera and scans QR codes.
 *
 * Props:
 *   open      — bool, controls dialog visibility
 *   onClose   — fn(): close dialog
 *   onDecode  — fn(text: string): called with decoded text when a QR is read
 *
 * Behaviour:
 *   - Auto starts back camera ("environment") on open.
 *   - Stops cleanly on close / unmount.
 *   - Surfaces permission/error states.
 */
export default function QRScannerDialog({ open, onClose, onDecode }) {
    const containerRef = useRef(null);
    const scannerRef = useRef(null);
    const startedRef = useRef(false);
    const [status, setStatus] = useState("idle"); // idle | starting | running | error
    const [errMsg, setErrMsg] = useState("");

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setStatus("starting");
        setErrMsg("");

        const start = async () => {
            try {
                const scanner = new Html5Qrcode("qr-scanner-region", { verbose: false });
                scannerRef.current = scanner;
                await scanner.start(
                    { facingMode: "environment" },
                    {
                        fps: 12,
                        qrbox: (vw, vh) => {
                            const min = Math.min(vw, vh);
                            const size = Math.floor(min * 0.7);
                            return { width: size, height: size };
                        },
                        aspectRatio: 1.0,
                    },
                    (decodedText) => {
                        if (cancelled) return;
                        // Stop immediately to prevent multiple fires
                        scanner.stop().catch(() => {}).finally(() => {
                            scannerRef.current = null;
                            startedRef.current = false;
                            onDecode?.(decodedText);
                        });
                    },
                    () => { /* ignore per-frame errors */ },
                );
                if (cancelled) {
                    await scanner.stop().catch(() => {});
                    return;
                }
                startedRef.current = true;
                setStatus("running");
            } catch (err) {
                if (cancelled) return;
                const m = String(err?.message || err);
                let friendly = m;
                if (/NotAllowedError|Permission denied/i.test(m)) {
                    friendly = "Akses kamera ditolak. Mohon izinkan akses kamera di browser.";
                } else if (/NotFoundError|Requested device not found/i.test(m)) {
                    friendly = "Kamera tidak ditemukan di perangkat ini.";
                } else if (/NotReadableError/i.test(m)) {
                    friendly = "Kamera sedang dipakai aplikasi lain. Tutup app lain & coba lagi.";
                } else if (/secure context|https/i.test(m)) {
                    friendly = "Kamera hanya bekerja di HTTPS atau localhost.";
                }
                setErrMsg(friendly);
                setStatus("error");
            }
        };
        start();

        return () => {
            cancelled = true;
            const s = scannerRef.current;
            if (s && startedRef.current) {
                s.stop().catch(() => {}).finally(() => {
                    scannerRef.current = null;
                    startedRef.current = false;
                });
            }
        };
    }, [open, onDecode]);

    if (!open) return null;

    return (
        <div data-testid="qr-scanner-dialog" className="fixed inset-0 z-50 bg-black/95 flex flex-col">
            <header className="flex items-center justify-between p-4 text-white">
                <div>
                    <h2 className="font-semibold text-lg">Pindai QR Produk</h2>
                    <p className="text-xs text-white/70">Arahkan kamera ke QR pada label produk</p>
                </div>
                <button
                    data-testid="qr-scanner-close"
                    onClick={onClose}
                    aria-label="Tutup scanner"
                    className="grid h-10 w-10 place-items-center rounded-full bg-white/15 hover:bg-white/25 text-white"
                >
                    <X size={20} />
                </button>
            </header>

            <div className="relative flex-1 overflow-hidden flex items-center justify-center">
                <div id="qr-scanner-region" ref={containerRef} className="w-full h-full" />

                {status === "starting" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
                        <Loader2 className="animate-spin" size={28} />
                        <span className="text-sm">Membuka kamera…</span>
                    </div>
                )}
                {status === "error" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white p-6 text-center">
                        <div className="grid h-14 w-14 place-items-center rounded-full bg-destructive/30">
                            <AlertCircle size={26} strokeWidth={2} />
                        </div>
                        <p className="text-sm leading-relaxed max-w-xs" data-testid="qr-scanner-error">{errMsg}</p>
                        <button onClick={onClose} className="rounded-full bg-white/20 hover:bg-white/30 px-5 py-2 text-sm">
                            Tutup
                        </button>
                    </div>
                )}
                {status === "running" && (
                    <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-white text-xs backdrop-blur">
                        <Camera size={14} strokeWidth={2} />
                        Posisikan QR di dalam kotak
                    </div>
                )}
            </div>
        </div>
    );
}
